const DEFAULT_TIMEOUT_MS = 8000

/**
 * Races a promise against a timeout, rejecting if it doesn't settle in time.
 *
 * On Cloudflare Workers, AbortController-based timeouts inside fetch libraries
 * such as storyblok-js-client do NOT reliably reject — the underlying request
 * hangs and the Worker never responds (low CPU, no error logs). Racing against
 * an explicit timer turns that hang into a catchable rejection.
 *
 * Rule of thumb: keep (sequential calls) x timeoutMs under ~20s to stay within
 * Cloudflare's request wall-clock limit.
 */
export const withTimeout = async <T>(
  work: Promise<T>,
  label: string,
  timeoutMs = DEFAULT_TIMEOUT_MS
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(
      () => { reject(new Error(`[fetch] ${label} timed out after ${timeoutMs}ms`)) },
      timeoutMs
    )
  })

  try {
    return await Promise.race([work, timeout])
  } finally {
    clearTimeout(timer)
  }
}

export const getEnvVariable = (variableName: string): string => {
  const variable = process.env[variableName]

  if (!variable) {
    throw new Error(`Missing environment variable: ${variableName}`)
  }

  return variable
}

export const castValidStringToNunber = (val: string | undefined): number => {
  if (!val) return 0
  return Number.isNaN(+val) ? 0 : +val
}

export const updateUrl = (path: string, params: Record<string, unknown>): void => {
  const url = new URL(path)
  Object.keys(params).forEach((key) => {
    const val = params[key] as string

    if (!val) {
      url.searchParams.delete(key)
    } else {
      url.searchParams.set(key, val)
    }
  })
  window.history.replaceState({}, '', url.toString())
}

export const createCononicalUrl = (slug: string): string => {
  if (!process.env.BASE_URL) {
    throw new Error('Missing environment variable: BASE_URL')
  }
  if (slug.startsWith('/')) {
    throw new Error(`Slug cannot start with a / for ${process.env.BASE_URL}/${slug}`)
  }

  const canonUrl = `${process.env.BASE_URL}/${slug}`

  try {
    const validUrl = new URL(canonUrl)
    return validUrl.toString()
  } catch (e) {
    const error = e as Error
    throw new Error(`Invalid canonical url, ${error?.message}`)
  }
}

export const shuffleArray = <T>(array: T[]): T[] => {
  const arrCopy = [...array]
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arrCopy[i], arrCopy[j]] = [arrCopy[j], arrCopy[i]]
  }

  return arrCopy
}
